import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetContractorQuery,
  useGetContractorAdminsQuery,
  useCreateContractorAdminMutation,
  useDeleteContractorAdminMutation,
  useGetInviteLinksQuery,
  useCreateInviteLinkMutation,
  useUpdateInviteLinkMutation,
  useApproveUserMutation,
  useRejectUserMutation,
} from './contractorsApiSlice';
import { useGetUsersQuery } from '../users/usersApiSlice';
import { Icon, Table, Loader } from 'semantic-ui-react';
import moment from 'moment';
import {
  ui,
  Pill,
  Badge,
  Modal,
  Select as UiSelect,
  InfoNote,
  cardStyle,
  fieldStyle,
  labelStyle,
} from '../../components/adminUi';

const th = { borderBottom: `1px solid ${ui.headerBorder}`, color: ui.muted, fontWeight: 600 };
const firstCell = { paddingLeft: '20px' };

const TableCard = ({ children }) => (
  <div style={cardStyle}>
    <Table compact="very" style={{ margin: 0, border: 'none' }}>{children}</Table>
  </div>
);

const emptyRow = (colSpan, text) => (
  <Table.Row>
    <Table.Cell colSpan={colSpan} textAlign="center" style={{ padding: '40px', color: ui.muted }}>
      {text}
    </Table.Cell>
  </Table.Row>
);

const ContractorDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: contractor, isLoading } = useGetContractorQuery(id);
  const { data: adminsData } = useGetContractorAdminsQuery({ contractor: id });
  const { data: linksData } = useGetInviteLinksQuery({ contractor: id });
  const { data: usersData } = useGetUsersQuery({ contractor: id, is_approved: false });
  const { data: allUsersData } = useGetUsersQuery({});

  const [createContractorAdmin] = useCreateContractorAdminMutation();
  const [deleteContractorAdmin] = useDeleteContractorAdminMutation();
  const [createInviteLink] = useCreateInviteLinkMutation();
  const [updateInviteLink] = useUpdateInviteLinkMutation();
  const [approveUser] = useApproveUserMutation();
  const [rejectUser] = useRejectUserMutation();

  const [activeTab, setActiveTab] = useState(0);
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [adminUserId, setAdminUserId] = useState('');
  const [createLinkModal, setCreateLinkModal] = useState(false);
  const [linkFormData, setLinkFormData] = useState({ expires_at: '', max_uses: '' });
  const [confirmRemoveAdmin, setConfirmRemoveAdmin] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  const admins = Array.isArray(adminsData) ? adminsData : adminsData?.results || [];
  const links = Array.isArray(linksData) ? linksData : linksData?.results || [];
  const users = Array.isArray(usersData) ? usersData : usersData?.results || [];
  const allUsers = Array.isArray(allUsersData) ? allUsersData : allUsersData?.results || [];

  const adminUserIds = new Set(admins.map(a => a.user));
  const userOptions = allUsers
    .filter(u => !adminUserIds.has(u.id))
    .map(u => ({ value: u.id, text: `${u.full_name || u.username} (${u.email})` }));

  if (isLoading) return <Loader active inline="centered" />;
  if (!contractor) {
    return (
      <div style={{ padding: '40px', color: ui.redFg }}>파트너를 찾을 수 없습니다.</div>
    );
  }

  const handleAddAdmin = async () => {
    try {
      await createContractorAdmin({ user: adminUserId, contractor: id }).unwrap();
      setAddAdminModal(false);
      setAdminUserId('');
    } catch (err) {
      console.error('Failed to add admin:', err);
    }
  };

  const handleRemoveAdmin = async () => {
    if (confirmRemoveAdmin) {
      try {
        await deleteContractorAdmin(confirmRemoveAdmin.id).unwrap();
      } catch (err) {
        console.error('Failed to remove admin:', err);
      }
      setConfirmRemoveAdmin(null);
    }
  };

  const handleCreateLink = async () => {
    try {
      const data = { contractor: id };
      if (linkFormData.expires_at) data.expires_at = linkFormData.expires_at;
      if (linkFormData.max_uses) data.max_uses = parseInt(linkFormData.max_uses, 10);
      await createInviteLink(data).unwrap();
      setCreateLinkModal(false);
      setLinkFormData({ expires_at: '', max_uses: '' });
    } catch (err) {
      console.error('Failed to create invite link:', err);
    }
  };

  const handleToggleLink = async (link) => {
    try {
      await updateInviteLink({ id: link.id, is_active: !link.is_active }).unwrap();
    } catch (err) {
      console.error('Failed to toggle link:', err);
    }
  };

  const getInviteUrl = (token) => `${window.location.origin}/onboarding?token=${token}`;

  const handleCopyLink = (token) => {
    navigator.clipboard.writeText(getInviteUrl(token));
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const tabs = [
    { label: '승인 대기', count: users.length },
    { label: '관리자', count: admins.length },
    { label: '초대 링크', count: links.length },
  ];

  const renderPendingTab = () => (
    <>
      <InfoNote>
        <Icon name="info circle" style={{ margin: 0, color: ui.accent, marginTop: '1px' }} />
        <span>
          이 파트너로 가입을 신청한 사용자입니다. <b>승인</b>하면 로그인과 학습을 시작할 수 있고,
          <b> 승인 취소</b>하면 접근이 차단됩니다.
        </span>
      </InfoNote>
      <TableCard>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ ...th, ...firstCell }}>이름</Table.HeaderCell>
            <Table.HeaderCell style={th}>이메일</Table.HeaderCell>
            <Table.HeaderCell style={th}>승인 상태</Table.HeaderCell>
            <Table.HeaderCell style={th}>가입일</Table.HeaderCell>
            <Table.HeaderCell style={th} textAlign="right">작업</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {users.map((user) => (
            <Table.Row key={user.id}>
              <Table.Cell style={firstCell}>{user.full_name || user.username}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{user.email}</Table.Cell>
              <Table.Cell>
                {user.is_approved
                  ? <Badge tone="green">승인됨</Badge>
                  : <Badge tone="amber">대기중</Badge>}
              </Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{moment(user.created_at).format('YYYY-MM-DD')}</Table.Cell>
              <Table.Cell textAlign="right">
                {!user.is_approved ? (
                  <Pill variant="success" onClick={() => approveUser(user.id)}>
                    <Icon name="check" style={{ margin: 0 }} /> 승인
                  </Pill>
                ) : (
                  <Pill variant="amber" onClick={() => rejectUser(user.id)}>
                    <Icon name="ban" style={{ margin: 0 }} /> 승인 취소
                  </Pill>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
          {users.length === 0 && emptyRow(5, '승인 대기 중인 사용자가 없습니다.')}
        </Table.Body>
      </TableCard>
    </>
  );

  const renderAdminsTab = () => (
    <>
      <InfoNote>
        <Icon name="info circle" style={{ margin: 0, color: ui.accent, marginTop: '1px' }} />
        <span>
          파트너 관리자는 해당 파트너 훈련생의 학습·평가 데이터를 조회할 수 있습니다.
          <b> 관리자 추가</b>로 사용자를 지정하고, 휴지통 아이콘으로 권한을 회수합니다.
        </span>
      </InfoNote>
      <div style={{ marginBottom: '12px' }}>
        <Pill variant="primary" onClick={() => setAddAdminModal(true)}>
          <Icon name="plus" style={{ margin: 0 }} /> 관리자 추가
        </Pill>
      </div>
      <TableCard>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ ...th, ...firstCell }}>이름</Table.HeaderCell>
            <Table.HeaderCell style={th}>이메일</Table.HeaderCell>
            <Table.HeaderCell style={th}>배정자</Table.HeaderCell>
            <Table.HeaderCell style={th}>배정일</Table.HeaderCell>
            <Table.HeaderCell style={th} textAlign="right">작업</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {admins.map((admin) => (
            <Table.Row key={admin.id}>
              <Table.Cell style={firstCell}>{admin.user_detail?.full_name || admin.user_detail?.username}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{admin.user_detail?.email}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{admin.assigned_by_detail?.full_name || '-'}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{moment(admin.assigned_at).format('YYYY-MM-DD')}</Table.Cell>
              <Table.Cell textAlign="right">
                <Pill variant="danger" style={{ padding: '7px 10px' }} onClick={() => setConfirmRemoveAdmin(admin)} title="관리자 제거">
                  <Icon name="trash" style={{ margin: 0 }} />
                </Pill>
              </Table.Cell>
            </Table.Row>
          ))}
          {admins.length === 0 && emptyRow(5, '지정된 관리자가 없습니다.')}
        </Table.Body>
      </TableCard>
    </>
  );

  const renderLinksTab = () => (
    <>
      <InfoNote>
        <Icon name="info circle" style={{ margin: 0, color: ui.accent, marginTop: '1px' }} />
        <span>
          이 링크로 가입한 사용자는 자동으로 이 파트너에 소속됩니다. 링크를 <b>복사</b>해 배포하고,
          만료일·최대 사용 횟수를 정하거나 <b>활성/비활성</b>으로 사용을 제어할 수 있습니다.
        </span>
      </InfoNote>
      <div style={{ marginBottom: '12px' }}>
        <Pill variant="primary" onClick={() => setCreateLinkModal(true)}>
          <Icon name="plus" style={{ margin: 0 }} /> 새 초대 링크
        </Pill>
      </div>
      <TableCard>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ ...th, ...firstCell }}>초대 링크</Table.HeaderCell>
            <Table.HeaderCell style={th}>상태</Table.HeaderCell>
            <Table.HeaderCell style={th}>사용 횟수</Table.HeaderCell>
            <Table.HeaderCell style={th}>만료일</Table.HeaderCell>
            <Table.HeaderCell style={th}>생성일</Table.HeaderCell>
            <Table.HeaderCell style={th} textAlign="right">작업</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {links.map((link) => (
            <Table.Row key={link.id}>
              <Table.Cell style={firstCell}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <code style={{ fontSize: '0.85em', color: ui.text }}>{link.token.substring(0, 16)}…</code>
                  <Pill variant="neutral" style={{ padding: '6px 9px' }} onClick={() => handleCopyLink(link.token)} title="링크 복사">
                    <Icon name={copiedToken === link.token ? 'check' : 'copy'} style={{ margin: 0 }} />
                  </Pill>
                </div>
              </Table.Cell>
              <Table.Cell>
                {link.is_valid
                  ? <Badge tone="green">활성</Badge>
                  : <Badge tone="gray">비활성</Badge>}
              </Table.Cell>
              <Table.Cell>{link.use_count}{link.max_uses ? ` / ${link.max_uses}` : ''}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{link.expires_at ? moment(link.expires_at).format('YYYY-MM-DD HH:mm') : '없음'}</Table.Cell>
              <Table.Cell style={{ color: ui.muted }}>{moment(link.created_at).format('YYYY-MM-DD')}</Table.Cell>
              <Table.Cell textAlign="right">
                <Pill variant={link.is_active ? 'amber' : 'success'} onClick={() => handleToggleLink(link)}>
                  {link.is_active ? '비활성화' : '활성화'}
                </Pill>
              </Table.Cell>
            </Table.Row>
          ))}
          {links.length === 0 && emptyRow(6, '생성된 초대 링크가 없습니다.')}
        </Table.Body>
      </TableCard>
    </>
  );

  const tabRenderers = [renderPendingTab, renderAdminsTab, renderLinksTab];

  return (
    <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header Section */}
      <div
        style={{
          flex: '0 0 auto',
          padding: '16px 0',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '0 20px' }}>
          <button
            onClick={() => navigate('/admin/contractors')}
            style={{
              backgroundColor: '#F2F2F7', border: 'none', borderRadius: '50%', width: '40px', flexShrink: 0, height: '40px',
              padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <Icon name="arrow left" style={{ margin: 0, color: ui.accent, fontSize: '18px' }} />
          </button>
          <div>
            <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: ui.text }}>{contractor.name}</h2>
            <p style={{ fontSize: '13px', color: ui.muted, margin: 0 }}>생성일: {moment(contractor.created_at).format('YYYY-MM-DD')}</p>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tab bar */}
        <div style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${ui.border}`, marginBottom: '20px' }}>
          {tabs.map((tab, i) => {
            const active = activeTab === i;
            return (
              <button
                key={tab.label}
                onClick={() => setActiveTab(i)}
                style={{
                  border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
                  padding: '10px 16px', fontSize: '14px', fontWeight: active ? 700 : 500,
                  color: active ? ui.accent : ui.muted, position: 'relative', marginBottom: '-1px',
                  borderBottom: active ? `2px solid ${ui.accent}` : '2px solid transparent',
                }}
              >
                {tab.label}
                <span style={{
                  marginLeft: '6px', fontSize: '12px', fontWeight: 600, padding: '1px 7px', borderRadius: '10px',
                  background: active ? ui.blueBg : '#F2F2F7', color: active ? ui.accent : ui.muted,
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {tabRenderers[activeTab]()}
        </div>
      </div>

      {/* Add Admin Modal */}
      <Modal
        open={addAdminModal}
        onClose={() => setAddAdminModal(false)}
        title="파트너 관리자 추가"
        footer={
          <>
            <Pill variant="ghost" onClick={() => setAddAdminModal(false)}>취소</Pill>
            <Pill variant="primary" onClick={handleAddAdmin} disabled={!adminUserId}>추가</Pill>
          </>
        }
      >
        <label style={labelStyle}>사용자 선택</label>
        <UiSelect value={adminUserId} onChange={e => setAdminUserId(e.target.value)}>
          <option value="">사용자를 선택하세요…</option>
          {userOptions.map(o => (
            <option key={o.value} value={o.value}>{o.text}</option>
          ))}
        </UiSelect>
      </Modal>

      {/* Create Invite Link Modal */}
      <Modal
        open={createLinkModal}
        onClose={() => setCreateLinkModal(false)}
        title="새 초대 링크 생성"
        footer={
          <>
            <Pill variant="ghost" onClick={() => setCreateLinkModal(false)}>취소</Pill>
            <Pill variant="primary" onClick={handleCreateLink}>생성</Pill>
          </>
        }
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>만료일시 (선택)</label>
          <input
            type="datetime-local"
            style={fieldStyle}
            value={linkFormData.expires_at}
            onChange={e => setLinkFormData(prev => ({ ...prev, expires_at: e.target.value }))}
          />
        </div>
        <div>
          <label style={labelStyle}>최대 사용 횟수 (선택)</label>
          <input
            type="number"
            min="1"
            placeholder="무제한"
            style={fieldStyle}
            value={linkFormData.max_uses}
            onChange={e => setLinkFormData(prev => ({ ...prev, max_uses: e.target.value }))}
          />
        </div>
      </Modal>

      {/* Confirm Remove Admin */}
      <Modal
        open={!!confirmRemoveAdmin}
        onClose={() => setConfirmRemoveAdmin(null)}
        title="관리자 제거"
        width={400}
        footer={
          <>
            <Pill variant="ghost" onClick={() => setConfirmRemoveAdmin(null)}>취소</Pill>
            <Pill variant="danger" onClick={handleRemoveAdmin}>제거</Pill>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: '14px', color: ui.text, lineHeight: 1.6 }}>
          <b>{confirmRemoveAdmin?.user_detail?.full_name || confirmRemoveAdmin?.user_detail?.username}</b> 님의 관리자 권한을 제거하시겠습니까?
        </p>
      </Modal>
    </div>
  );
};

export default ContractorDetail;
